import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { useApi } from '@/context/AuthContext';
import { formatIndianCurrency, INDIAN_STATES, validateGSTIN, getErrorMessage } from '@/utils/helpers';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Building2 } from 'lucide-react';

const TDS_SECTIONS = [
  { value: '194C', label: '194C - Contractor (1%/2%)' },
  { value: '194J', label: '194J - Professional (10%)' },
  { value: '194Q', label: '194Q - Purchase (0.1%)' },
  { value: '195', label: '195 - Foreign (20%)' },
];

export default function VendorsPage() {
  const api = useApi();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gstin: '',
    pan: '',
    state_code: '',
    billing_address: { line1: '', city: '', state: '', pincode: '' },
    tds_applicable: false,
    tds_section: '',
    tds_rate: 0
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendors');
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      gstin: '',
      pan: '',
      state_code: '',
      billing_address: { line1: '', city: '', state: '', pincode: '' },
      tds_applicable: false,
      tds_section: '',
      tds_rate: 0
    });
    setEditingVendor(null);
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name,
      email: vendor.email || '',
      phone: vendor.phone || '',
      gstin: vendor.gstin || '',
      pan: vendor.pan || '',
      state_code: vendor.state_code || '',
      billing_address: vendor.billing_address || { line1: '', city: '', state: '', pincode: '' },
      tds_applicable: vendor.tds_applicable || false,
      tds_section: vendor.tds_section || '',
      tds_rate: vendor.tds_rate || 0
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    
    if (formData.gstin && !validateGSTIN(formData.gstin)) {
      toast.error('Invalid GSTIN format');
      return;
    }

    try {
      if (editingVendor) {
        await api.put(`/vendors/${editingVendor.id}`, formData);
        toast.success('Vendor updated');
      } else {
        await api.post('/vendors', formData);
        toast.success('Vendor added');
      }
      fetchVendors();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save vendor'));
    }
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await api.delete(`/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const filteredVendors = vendors.filter(v => 
    v.name?.toLowerCase().includes(search.toLowerCase()) ||
    v.gstin?.toLowerCase().includes(search.toLowerCase()) ||
    v.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-stone-200 rounded w-1/3"></div>
          <div className="h-64 bg-stone-200 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vendors-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-['Outfit']">Vendors</h1>
            <p className="text-stone-600">Manage your supplier directory</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1D9E75] hover:bg-[#178a65] text-white btn-press gap-2" data-testid="add-vendor-btn">
                <Plus className="w-4 h-4" /> Add Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">
                  {editingVendor ? 'Edit Vendor' : 'Add Vendor'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="vendor-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Vendor Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Supplier Inc"
                      required
                      data-testid="vendor-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="vendor@company.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <Label>GSTIN</Label>
                    <Input
                      value={formData.gstin}
                      onChange={(e) => setFormData(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
                      placeholder="27AABCU9603R1ZM"
                      className="font-mono"
                    />
                  </div>
                  <div>
                    <Label>PAN</Label>
                    <Input
                      value={formData.pan}
                      onChange={(e) => setFormData(prev => ({ ...prev, pan: e.target.value.toUpperCase() }))}
                      placeholder="AABCU9603R"
                      className="font-mono"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>State</Label>
                    <Select 
                      value={formData.state_code} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, state_code: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map(state => (
                          <SelectItem key={state.code} value={state.code}>
                            {state.code} - {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* TDS Section */}
                  <div className="col-span-2 pt-4 border-t">
                    <div className="flex items-center gap-2 mb-4">
                      <Checkbox
                        id="tds_applicable"
                        checked={formData.tds_applicable}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tds_applicable: checked }))}
                      />
                      <Label htmlFor="tds_applicable" className="cursor-pointer">TDS Applicable</Label>
                    </div>
                    
                    {formData.tds_applicable && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>TDS Section</Label>
                          <Select 
                            value={formData.tds_section} 
                            onValueChange={(v) => setFormData(prev => ({ ...prev, tds_section: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select section" />
                            </SelectTrigger>
                            <SelectContent>
                              {TDS_SECTIONS.map(section => (
                                <SelectItem key={section.value} value={section.value}>
                                  {section.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>TDS Rate (%)</Label>
                          <Input
                            type="number"
                            value={formData.tds_rate}
                            onChange={(e) => setFormData(prev => ({ ...prev, tds_rate: parseFloat(e.target.value) || 0 }))}
                            min="0"
                            max="30"
                            step="0.1"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1D9E75] hover:bg-[#178a65] text-white" data-testid="save-vendor-btn">
                    {editingVendor ? 'Update' : 'Add'} Vendor
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        {filteredVendors.length > 0 ? (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>TDS</TableHead>
                  <TableHead className="text-right">Total Spend</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((vendor) => (
                  <TableRow key={vendor.id}>
                    <TableCell className="font-medium">{vendor.name}</TableCell>
                    <TableCell className="font-mono text-sm">{vendor.gstin || '-'}</TableCell>
                    <TableCell>{vendor.email || '-'}</TableCell>
                    <TableCell>
                      {vendor.tds_applicable ? (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                          {vendor.tds_section} @ {vendor.tds_rate}%
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatIndianCurrency(vendor.total_spend || 0)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(vendor)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(vendor.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 p-12">
            <div className="text-center">
              <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-700 mb-2">No vendors yet</h3>
              <p className="text-stone-500 mb-6">Add your suppliers to track expenses and TDS</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-[#1D9E75] hover:bg-[#178a65] text-white gap-2">
                <Plus className="w-4 h-4" /> Add Vendor
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
